/**
 * @description Per-page SEO metadata (title, description, keywords, robots, canonical)
 *              for the public website's static routes.
 *
 *              Sourced from the "Pages for Title" sheet supplied by marketing. The sheet is
 *              the source of truth for this copy - edit it there first, then here. Canonical
 *              URLs are derived from the route path rather than `window.location`, so
 *              hash deep-links (`/people#...`), tab views and tracking query strings all
 *              self-canonicalize to the base URL.
 * @filename page-meta.ts
 */

import { CANONICAL_ORIGIN } from "./site-urls";
import { HOMEPAGE_META_DESCRIPTION, HOMEPAGE_TITLE } from "./homepage-meta";

export interface PageMeta {
  /** Contents of <title> and og:title. */
  title: string;
  /** Contents of <meta name="description"> and og:description. */
  description: string;
  /** Contents of <meta name="keywords">. */
  keywords: string;
  /** Contents of <meta name="robots">. */
  robots: string;
}

/** Route path (no trailing slash, "/" for home) -> metadata. */
export const PAGE_META: { readonly [path: string]: PageMeta } = {
  // --- Core / marketing (prerendered) ---
  "/": {
    title: "Private Market & Startup Investing Network | Radivision",
    description:
      "Radivision is the entrepreneurship network for private market and IPO access. Discover startups, watch original series, connect with founders.",
    keywords:
      "startup investing, private market investing, pre-IPO investing, entrepreneurship network, startup pitches, founders and investors, venture capital, angel investing, Pitch Planet, Radivision",
    robots: "index, follow",
  },
  "/about": {
    title: "About Radivision | The Entrepreneurship Network",
    description:
      "Learn how Radivision opens private market and IPO access to everyone. Meet the team, the mission, and the story behind the entrepreneurship network.",
    keywords:
      "about Radivision, entrepreneurship network, private market access, startup media platform, Radivision mission, Radivision team, democratizing investing, retail investor access",
    robots: "index, follow",
  },
  "/advocacy": {
    title: "Capital Markets Policy Advocacy | Radivision",
    description:
      "Radivision advocates for open, functional capital markets: IPO and private market reforms to restore capital formation, liquidity and wealth access for all.",
    keywords:
      "capital markets reform, IPO reform, private market access, retail investor access, SEC policy submissions, testing the waters, Executive Order 14330, wealth inequality, capital formation, Radivision advocacy",
    robots: "index, follow",
  },
  "/blog": {
    title: "Radivision Blog | Media Coverage & Startup News",
    description:
      "Radivision is reshaping startup private and IPO markets so capital and opportunity reach everyone, not just the few. Explore our latest posts and coverage.",
    keywords:
      "Radivision blog, media coverage, startup news, private markets, IPO reform, Mona DeFrawi, entrepreneurship, fintech news, capital formation",
    robots: "index, follow",
  },
  "/blog/2025-new-startup-ipo-funding": {
    title: "2025: New Startup & IPO Funding | Radivision Blog",
    description: "2025: New Startup & IPO Funding \u2014 Radivision blog.",
    keywords:
      "Radivision blog, Article, Blog, Newsletter, startup news, private markets",
    robots: "index, follow",
  },
  "/blog/radivisionand-kingscrowd-partner-to-support-new-startup-fundingand-private-market-investing": {
    title: "Radivision®and KingsCrowd Partner to Support New Startup Fundingand Private Market Investing | Radivision Blog",
    description: "Radivision\u00aeand KingsCrowd Partner to Support New Startup Fundingand Private Market Investing \u2014 Radivision blog.",
    keywords:
      "Radivision blog, Article, Blog, Press Release, startup news, private markets",
    robots: "index, follow",
  },
  "/blog/radivision-and-esms-global-ceos-mona-defrawi-and-bill-kiser-unite-to-drive-startup-funding-and-retail-investor-prosperity": {
    title: "Radivision and ESMS Global CEOs Mona DeFrawi and Bill Kiser Unite to Drive Startup Funding and Retail Investor Prosperity | Radivision Blog",
    description: "Radivision and ESMS Global CEOs Mona DeFrawi and Bill Kiser Unite to Drive Startup Funding and Retail Investor Prosperity \u2014 Radivision blog.",
    keywords:
      "Radivision blog, Article, Blog, Press Release, startup news, private markets",
    robots: "index, follow",
  },
  "/blog/meet-the-real-superheroes": {
    title: "Meet The REAL Superheroes | Radivision Blog",
    description: "Meet The REAL Superheroes \u2014 Radivision blog.",
    keywords:
      "Radivision blog, Article, Blog, Newsletter, startup news, private markets",
    robots: "index, follow",
  },
  "/blog/premiering-now-the-entrepreneurship-network": {
    title: "Premiering Now: The Entrepreneurship Network | Radivision Blog",
    description: "Premiering Now: The Entrepreneurship Network \u2014 Radivision blog.",
    keywords:
      "Radivision blog, Article, Blog, Newsletter, startup news, private markets",
    robots: "index, follow",
  },
  "/blog/infographic-democratizing-the-private-markets": {
    title: "Infographic: Restoring Capital Formation, Liquidity & Wealth Access for Everyone | Radivision Blog",
    description: "Infographic: Restoring Capital Formation, Liquidity & Wealth Access for Everyone \u2014 Radivision blog.",
    keywords:
      "Radivision blog, Article, Blog, Infographic, startup news, private markets",
    robots: "index, follow",
  },
  "/partners": {
    title: "Startup Ecosystem Partners & Sponsors | Radivision",
    description:
      "Meet the venture firms, accelerators, and brands partnering with Radivision to connect founders, investors, and the private market ecosystem.",
    keywords:
      "startup ecosystem partners, venture capital partners, accelerator partners, startup sponsors, corporate innovation partners, partnership opportunities, Radivision partners",
    robots: "index, follow",
  },
  "/get-started": {
    title: "Get Started on Radivision | Join the Network Free",
    description:
      "Create a free Radivision account in minutes. Follow founders and companies, save pitches, and get private market and IPO opportunities in your feed.",
    keywords:
      "create free account, join startup network, sign up Radivision, investor account, founder account, startup community signup, get started investing",
    robots: "index, follow",
  },
  "/support": {
    title: "Support & Help Center | Radivision",
    description:
      "Get help with your Radivision account, report a technical issue, or contact the team. Answers on profiles, pitches, billing, and platform access.",
    keywords:
      "Radivision support, help center, contact Radivision, account help, technical support, report an issue, customer service, FAQ",
    robots: "index, follow",
  },
  "/legal": {
    title: "Terms, Privacy & Cookie Policy | Radivision",
    description:
      "Read Radivision's terms and conditions, privacy policy, and cookie policy covering how the platform collects, uses, and protects your information.",
    keywords:
      "terms and conditions, privacy policy, cookie policy, user agreement, data protection, GDPR, CCPA, Radivision legal",
    robots: "index, follow",
  },
  "/pitchplanet": {
    title: "Pitch Planet | Watch Startup Pitches & Submit Yours",
    description:
      "Pitch Planet is Radivision's startup pitch showcase. Watch founders pitch live, submit your own pitch, and get in front of active investors.",
    keywords:
      "Pitch Planet, startup pitch platform, submit a pitch, pitch competition, watch startup pitches, founder pitch videos, pitch to investors, startup showcase",
    robots: "index, follow",
  },
  // --- Discovery / browse ---
  "/search": {
    title: "Search Startups, Founders & Investors | Radivision",
    description:
      "Search Radivision for startups, founders, investors, videos, and podcasts across the private market and entrepreneurship ecosystem.",
    keywords:
      "search startups, find founders, find investors, startup search engine, company search, investor search, Radivision search",
    robots: "noindex, follow",
  },
  "/people": {
    title: "Founders, Investors & Visionaries Directory | Radivision",
    description:
      "Browse thousands of founders, investors, and visionaries on Radivision. Explore profiles, pitches, and portfolios across the startup ecosystem.",
    keywords:
      "founders directory, investor directory, startup founders, angel investors, venture capitalists, entrepreneur profiles, visionaries, startup leaders",
    robots: "index, follow",
  },
  "/companies": {
    title: "Startup & Company Directory | Radivision",
    description:
      "Browse startups and companies across the private markets. Explore Radivision company profiles with pitches, funding context, and founder detail.",
    keywords:
      "startup directory, company directory, startup profiles, private companies, emerging companies, startups to watch, pre-IPO companies",
    robots: "index, follow",
  },
  "/navigate": {
    title: "Explore Curated Startup Lists | Radivision",
    description:
      "Navigate curated Radivision lists across industries, stages, and themes. Find the startups, investors, and stories worth watching right now.",
    keywords:
      "curated startup lists, startup collections, top startups, investor lists, startup rankings, browse startups, best startups to watch",
    robots: "index, follow",
  },
  "/ecosystem": {
    title: "The Startup Ecosystem, Mapped | Radivision",
    description:
      "See how founders, investors, accelerators, and partners connect. Radivision maps the private market ecosystem in one browsable view.",
    keywords:
      "startup ecosystem, venture ecosystem map, founders and investors network, accelerators, incubators, private market ecosystem, innovation ecosystem",
    robots: "index, follow",
  },
  "/learn": {
    title: "Learn Startup, Private Market & IPO Basics | Radivision",
    description:
      "Learn how private markets, startup fundraising, and IPOs actually work. Radivision guides, original series, and expert content for new investors.",
    keywords:
      "learn startup investing, private markets explained, IPO basics, startup fundraising, investor education, venture capital 101, how to invest in startups",
    robots: "index, follow",
  },
  "/buy": {
    title: "Membership Plans & Pricing | Radivision",
    description:
      "Compare Radivision membership options and unlock full access to pitches, original series, and private market resources. Choose the plan that fits.",
    keywords:
      "Radivision membership, pricing, subscription plans, premium access, upgrade account, investor membership, plan comparison",
    robots: "index, follow",
  },
  "/tour": {
    title: "Take a Tour of Radivision | How the Platform Works",
    description:
      "See how Radivision works in a short walkthrough. Discover startups, follow founders, watch original series, and track private market opportunities.",
    keywords:
      "how Radivision works, platform tour, product walkthrough, features overview, getting started guide, demo",
    robots: "index, follow",
  },
  // --- Dynamic entity pages ---
  "/exclusive-events": {
    title: "Exclusive Founder & Investor Events | Radivision",
    description:
      "Invite-only Radivision events connecting founders and investors. See upcoming sessions, request an invitation, and register for the next room.",
    keywords:
      "startup events, investor events, founder networking events, invite-only events, virtual startup events, pitch events, private market events",
    robots: "index, follow",
  },
  // --- Authenticated user pages ---
  "/account": {
    title: "Account Settings | Radivision",
    description:
      "Manage your Radivision account - profile details, password, email preferences, and notification settings.",
    keywords:
      "account settings, profile settings, manage account, change password, email preferences, notification settings",
    robots: "noindex, nofollow",
  },
  "/saved": {
    title: "Saved Items | Radivision",
    description: "Your saved Radivision pitches, companies, founders, and episodes, bookmarked in one place.",
    keywords: "saved items, bookmarks, saved startups, saved pitches, watchlist, my library",
    robots: "noindex, nofollow",
  },
  "/following": {
    title: "Following | Radivision",
    description:
      "The founders, investors, and companies you follow on Radivision, with their latest pitches, posts, and updates.",
    keywords: "following, followed founders, followed companies, personalized feed, my network, updates",
    robots: "noindex, nofollow",
  },
  // --- Campaign / editorial collections (CMS-authored) ---
  "/sheconomics": {
    title: "SHEconomics | Women Founders & Investors | Radivision",
    description:
      "SHEconomics spotlights the women founders and investors reshaping the private markets. Watch the series and explore the full Radivision collection.",
    keywords:
      "SHEconomics, women founders, women investors, female entrepreneurs, women in venture capital, women-led startups, gender funding gap, funding for women",
    robots: "index, follow",
  },
  "/black-is-the-new-green": {
    title: "Black Is The New Green | Black Founders | Radivision",
    description:
      "Black Is The New Green features Black founders and investors building wealth through entrepreneurship. Watch the collection on Radivision.",
    keywords:
      "Black Is The New Green, Black founders, Black investors, Black entrepreneurship, minority-owned startups, generational wealth, diverse founders",
    robots: "index, follow",
  },
  "/radical-visionaries": {
    title: "Radical Visionaries | Bold Founder Stories | Radivision",
    description:
      "Radical Visionaries profiles the founders rewriting the rules of their industries. Watch their stories and pitches on Radivision.",
    keywords:
      "Radical Visionaries, visionary founders, disruptive startups, founder stories, innovation leaders, industry disruptors",
    robots: "index, follow",
  },
  "/covidpreneurs": {
    title: "Covidpreneurs | Founders Built in Crisis | Radivision",
    description:
      "Covidpreneurs tells the stories of founders who launched and scaled through the pandemic. Watch the collection on Radivision.",
    keywords:
      "Covidpreneurs, pandemic startups, founders during COVID, crisis entrepreneurship, resilient startups, businesses built in 2020",
    robots: "index, follow",
  },
};

/**
 * Routes whose path carries a variable segment but whose copy is fixed. Matched by prefix.
 * The invitation token must never reach the index, hence `noindex, nofollow` here. This is
 * deliberately not paired with a `Disallow` in robots.txt: blocking the crawl would stop
 * Googlebot ever reading the noindex, leaving any already-indexed token URL stuck there.
 */
export const PREFIX_PAGE_META: ReadonlyArray<{ prefix: string; meta: PageMeta }> = [
  {
    prefix: "/invitation",
    meta: {
      title: "Your Radivision Invitation | Claim Your Access",
      description: "Redeem your Radivision invitation and finish setting up your account.",
      keywords: "Radivision invitation, claim invite, join by invitation, invite code, private access",
      robots: "noindex, nofollow",
    },
  },
];

/** Route prefixes whose metadata is rendered from fetched data by the route's own component. */
const OWNS_OWN_META: readonly string[] = ["/person", "/company"];

/**
 * Placeholder for an `OWNS_OWN_META` route before its profile has loaded, and for one whose
 * fetch failed.
 *
 * Something must always be rendered: react-helmet strips every tag it owns as soon as no
 * mounted instance declares one, so leaving these routes tagless left the page with a stale
 * title and no canonical at all after a client-side navigation, and with the index.html
 * homepage fallbacks on a cold load. `noindex` is the honest signal for a page that has no
 * content yet - the profile controller replaces this with `index, follow` and the real
 * canonical the moment its data arrives.
 */
export const UNRESOLVED_PROFILE_META: PageMeta = {
  title: HOMEPAGE_TITLE,
  description: HOMEPAGE_META_DESCRIPTION,
  keywords: "Radivision, founder profile, company profile, startup profile",
  robots: "noindex, follow",
};

/**
 * Used for any route not listed above, so an unmapped page never inherits another page's
 * tags. Kept `index, follow` on purpose: the CMS can publish an alias that is not yet in
 * the sheet, and defaulting those to `noindex` would quietly drop real pages out of the
 * index. The cost is that a genuine 404 alias stays indexable, which is no worse than
 * today - it at least now self-canonicalizes instead of pointing at the homepage.
 */
export const DEFAULT_PAGE_META: PageMeta = {
  title: HOMEPAGE_TITLE,
  description: HOMEPAGE_META_DESCRIPTION,
  keywords: "Radivision, entrepreneurship network, startup investing, private market investing, founders, investors",
  robots: "index, follow",
};

/**
 * Normalizes a browser pathname to the key format used by `PAGE_META`:
 * strips the trailing slash and collapses an empty path to "/".
 */
export function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

function matchesPrefix(normalized: string, prefix: string): boolean {
  return normalized === prefix || normalized.indexOf(`${prefix}/`) === 0;
}

/** Absolute self-canonical URL for a route path. */
export function canonicalUrlForPath(pathname: string): string {
  const normalized = normalizePath(pathname);
  return normalized === "/" ? `${CANONICAL_ORIGIN}/` : `${CANONICAL_ORIGIN}${normalized}`;
}

/**
 * Metadata for a route path. Returns `undefined` for routes whose metadata is built from
 * fetched data (person and company profiles) so their own components stay the single
 * source of truth for those tags.
 */
export function getPageMeta(pathname: string): PageMeta | undefined {
  const normalized = normalizePath(pathname);
  if (OWNS_OWN_META.some((prefix) => matchesPrefix(normalized, prefix))) {
    return undefined;
  }
  const exact = PAGE_META[normalized];
  if (exact) {
    return exact;
  }
  const byPrefix = PREFIX_PAGE_META.filter((entry) => matchesPrefix(normalized, entry.prefix))[0];
  return byPrefix ? byPrefix.meta : DEFAULT_PAGE_META;
}
